import {useState, useEffect, useRef} from "react";
import {IYear} from "../../utils/interfaces/IYear";
import YearRow from "./YearRow";
import {motion} from 'framer-motion';
import useApiGetMonthlyInvoices from "./api-hooks/useApiGetMonthlyInvoices";
import {CustomAxiosError} from "../../logics/api-config";
import {modalVariants} from "./ui/ModalVariant";
import MonthlyInvoicesList from "./MontlyInvoices/MonthlyInvoicesList";
import {useAccounting} from "../../utils/contexts/accounting_context";
import RestaurateurSelect from "./ui/selects/ResturateurSelect";
import InputFieldFilter from "./ui/InputFieldFilter";
import {IPeriod} from "../../utils/interfaces/IPeriod";
import {IInvoice} from "../../utils/interfaces/IInvoice";
import ButtonAccordionHandler from "./ui/ButtonAccordionHandler";
import AccordionMotionDiv from "./ui/AccordionMotionDiv";
import Loading from "./ui/Loading";
import MonthlyInvoiceForm from "./MontlyInvoices/MonthlyInvoiceForm";
import {IMonthlyInvoice} from "../../utils/interfaces/MonthlyInvoice/IMonthlyInvoice";
import MonthlyInvoiceRow from "./MontlyInvoices/MonthlyInvoiceRow";
import {IRestaurateur} from "../../utils/interfaces/IRestaurateur";


function YearsList() {
    const {
        isMonthlyInvoiceOverlayOpen,
        selectedInvoice,
        isMonthlyInvoiceFormOpen,
        selectIsMonthlyInvoiceFormOpen,
    } = useAccounting()

    const [isAccordionFiltersOpen, setIsAccordionFiltersOpen] = useState<boolean>(false)

    const shouldFocusInput = true;
    const inputRef = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    const {data, isLoading, isError, error} = useApiGetMonthlyInvoices();

    if (isError) {
        if (error.isAxiosError) {
            const axiosError = error as CustomAxiosError;
            console.log(axiosError.response.data.message);
        } else {
            console.log("Unknown error:", error);
        }
    }

    const [filterRestaurateur, setFilterRestaurateur] = useState<IRestaurateur>();
    const handleRestaurateurIdFilterChange = (restaurateur: IRestaurateur) => {
        setFilterRestaurateur(restaurateur);
    };

    function filterDataByRestaurateurFilter(data: IYear[], filter: number | undefined): IYear[] {
        if (filter === undefined) {
            // setFilteredMonthlyInvoices(undefined);
            return data;
        }

        const filteredData: IYear[] = [];

        data.forEach((year) => {
            const filteredPeriods: IPeriod[] = [];

            year.periods.forEach((period) => {
                const filteredInvoices: IInvoice[] = period.invoices.filter((invoice) =>
                    invoice.restaurateur.id.toString() === filter.toString()
                );


                if (filteredInvoices.length > 0) {
                    filteredPeriods.push({
                        ...period,
                        invoices: filteredInvoices,
                    });
                }
            });

            if (filteredPeriods.length > 0) {
                filteredData.push({
                    ...year,
                    periods: filteredPeriods,
                });
            }
        });


        return filteredData;
    }

    const [filterInvoice, setFilterInvoice] = useState<string>('');
    const handleInvoiceFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFilterInvoice(event.target.value);
    };

    function filterDataByInvoiceFilter(data: IYear[], filter: string): IYear[] {
        if (filter === '') {
            setFilteredMonthlyInvoices(undefined);
            return data;
        }

        const filteredData: IYear[] = [];

        data.forEach((year) => {
            const filteredPeriods: IPeriod[] = [];

            year.periods.forEach((period) => {
                const filteredInvoices: IInvoice[] = period.invoices.filter((invoice) =>
                    invoice.restaurateur.monthly_invoices.some((monthly_invoice) => {
                        const concatenatedInfo = monthly_invoice.invoice_info.toLowerCase() + monthly_invoice.invoice_id;
                        return concatenatedInfo.includes(filter.toLowerCase());
                    })
                );

                if (filteredInvoices.length > 0) {
                    filteredPeriods.push({
                        ...period,
                        invoices: filteredInvoices,
                    });
                }
            });

            if (filteredPeriods.length > 0) {
                filteredData.push({
                    ...year,
                    periods: filteredPeriods,
                });
            }
        });

        pushMonthlyInvoicesToFilteredArray(filteredData);

        return filteredData;
    }

    function pushMonthlyInvoicesToFilteredArray(data: IYear[]) {
        const monthlyInvoicesArray: IMonthlyInvoice[] = [];

        data.forEach((year) => {
            year.periods.forEach((period) => {
                period.invoices.forEach((invoice) => {
                    invoice.restaurateur.monthly_invoices.forEach((monthlyInvoice) => {
                        if (!monthlyInvoicesArray.some((existingInvoice) => existingInvoice.id === monthlyInvoice.id)) {
                            const monthlyInvoiceWithRestaurateur = {
                                ...monthlyInvoice,
                                restaurateur_name: invoice.restaurateur.name,
                            };
                            monthlyInvoicesArray.push(monthlyInvoiceWithRestaurateur);
                        }
                    });
                });
            });
        });

        setFilteredMonthlyInvoices(monthlyInvoicesArray);
    }


    const [filteredData, setFilteredData] = useState<IYear[] | undefined>(data);
    const [filteredMonthlyInvoices, setFilteredMonthlyInvoices] = useState<IMonthlyInvoice[] | undefined>();

    const mapped_objs = filteredData?.map((obj: IYear, index) => (
        <motion.div
            key={obj.year}
            initial={{x: '150%'}}
            animate={{x: '0%'}}
            transition={{duration: 0.2, delay: index * 0.03}}
        >
            <YearRow obj={obj}/>
        </motion.div>
    ));

    const mapped_monthly_invoices = filteredMonthlyInvoices?.map((obj: IMonthlyInvoice, index) => (
        <motion.div
            key={obj.year}
            initial={{x: '150%'}}
            animate={{x: '0%'}}
            transition={{duration: 0.2, delay: index * 0.03}}
        >
            <MonthlyInvoiceRow obj={obj} restaurateur_name={obj.restaurateur_name}/>
        </motion.div>
    ));

    useEffect(() => {
        if (data) {
            const filteredByRestaurateur = filterDataByRestaurateurFilter(data, filterRestaurateur?.id);
            const filteredByInvoiceData = filterDataByInvoiceFilter(filteredByRestaurateur, filterInvoice);
            setFilteredData(filteredByInvoiceData);
        }
    }, [data, filterInvoice, filterRestaurateur]);


    return (
        <>
            {isLoading ? (
                <Loading/>
            ) : (
                <>

                    {!isMonthlyInvoiceOverlayOpen &&
                        <div className='flex flex-row'>
                            <div className='mb-2'>
                                <ButtonAccordionHandler
                                    onClick={() => setIsAccordionFiltersOpen(!isAccordionFiltersOpen)}
                                    isOpen={isAccordionFiltersOpen}
                                    title={'FILTERS'}
                                />
                            </div>
                            <div className="flex-col ml-2">
                                {filterRestaurateur && (
                                    <div className="flex-col text-[11px]">
                                        Filter By Restaurateur: {filterRestaurateur.name}
                                    </div>
                                )}
                                {filterInvoice && (
                                    <div className="flex-col flex-col text-[11px]">
                                        Filter By Invoice data: {filterInvoice}
                                    </div>
                                )}
                            </div>
                        </div>


                    }


                    {isAccordionFiltersOpen && !isMonthlyInvoiceOverlayOpen &&
                        <AccordionMotionDiv isOpen={isAccordionFiltersOpen}>
                            <div className='flex flex-col filter-div'>
                                <div className="flex-1">
                                    <RestaurateurSelect
                                        restaurateur={filterRestaurateur}
                                        onChange={handleRestaurateurIdFilterChange}
                                        className={''}/>
                                </div>
                                <div className="flex-1">
                                    <InputFieldFilter
                                        placeholder="Invoice filter..."
                                        value={filterInvoice}
                                        onChange={handleInvoiceFilterChange}
                                        inputRef={shouldFocusInput ? inputRef : undefined}
                                    />
                                </div>
                            </div>

                        </AccordionMotionDiv>
                    }

                    {/*{!isMonthlyInvoiceOverlayOpen && mapped_objs}*/}

                    {
                        !isMonthlyInvoiceOverlayOpen
                            ? (mapped_monthly_invoices ? mapped_monthly_invoices : mapped_objs)
                            : null
                    }

                    {isMonthlyInvoiceOverlayOpen && selectedInvoice && (
                        <div className="left-modal-content">
                            <MonthlyInvoicesList/>
                        </div>
                    )}

                    {isMonthlyInvoiceFormOpen && (
                        <motion.div
                            className="modal-overlay"
                            initial="hidden"
                            animate="visible"
                            variants={modalVariants}
                        >
                            <div className="right-modal-content">
                                <MonthlyInvoiceForm
                                    onClose={() => selectIsMonthlyInvoiceFormOpen(false)}
                                />
                            </div>
                        </motion.div>
                    )}
                </>
            )}
        </>
    );

}

export default YearsList;